import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  SelectChangeEvent,
  Button,
  Collapse,
  IconButton,
  Divider,
  Rating,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  CardActions,
  CardHeader,
  Paper,
  LinearProgress,
  Stack,
  Badge,
  useTheme,
  Alert,
  Fade,
  Grow,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
  Tab,
  Tabs
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HotelIcon from '@mui/icons-material/Hotel';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import TableViewIcon from '@mui/icons-material/TableView';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '@services';
import { Hotel, RenovationEntry } from '@types';
import { useHotelList, useHotelUIState } from '@features/hotels/hooks';

type SortOption = 'name' | 'rooms' | 'stars' | 'renovations';
type SortDirection = 'asc' | 'desc';

const getPropertyTypeColor = (type: string | undefined): 'primary' | 'secondary' | 'success' | 'info' | 'warning' => {
  switch (type?.toLowerCase()) {
    case 'full service':
      return 'primary';
    case 'limited service':
      return 'info';
    case 'extended stay':
      return 'secondary';
    case 'resort':
      return 'success';
    default:
      return 'warning';
  }
};

// Generate initials from hotel name
const getHotelInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Get background color for avatar based on hotel name
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#c2185b',
    '#00796b', '#f57c00', '#607d8b', '#0288d1', '#689f38'
  ];
  const index = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};

const LoadingSkeleton = () => (
  <Grid container spacing={3}>
    {[1, 2, 3, 4, 5, 6].map((key) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={32} />
              <Skeleton variant="text" width="50%" />
            </Box>
          </Box>
          <Divider />
          <CardContent sx={{ pt: 2, pb: 1 }}>
            <Box display="flex" gap={1} mb={2}>
              <Skeleton variant="rounded" width={90} height={24} />
              <Skeleton variant="rounded" width={100} height={24} />
            </Box>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={60} sx={{ mt: 1, borderRadius: 1 }} />
          </CardContent>
          <Box p={2} mt="auto" borderTop={1} borderColor="divider">
            <Skeleton variant="rounded" width="100%" height={36} />
          </Box>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const HotelList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Use the extracted hooks
  const {
    hotels,
    filteredHotels,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    sortDirection,
    toggleSortDirection,
    successMessage,
    setSuccessMessage,
    deleteHotel,
    setError
  } = useHotelList();

  const {
    expanded,
    handleExpandClick,
    deleteDialog,
    handleDeleteClick: uiHandleDeleteClick,
    closeDeleteDialog,
    viewMode,
    setViewMode
  } = useHotelUIState();

  const handleAddHotel = () => {
    navigate('/add-hotel');
  };

  const handleDeleteClick = (hotel: Hotel, event: React.MouseEvent) => {
    uiHandleDeleteClick(hotel.id, hotel.name, event);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.hotelId) return;

    const success = await deleteHotel(deleteDialog.hotelId);
    if (success) {
      closeDeleteDialog();
      setSuccessMessage(`"${deleteDialog.hotelName}" has been deleted successfully.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (event: SelectChangeEvent<SortOption>) => {
    setSortBy(event.target.value as SortOption);
  };

  // Calculate portfolio metrics
  const totalRooms = hotels.reduce((sum, hotel) => sum + (hotel.num_rooms || 0), 0);
  const averageStarRating = hotels.length > 0
    ? hotels.reduce((sum, hotel) => sum + (hotel.star_rating || 0), 0) / hotels.length
    : 0;
  const totalRenovationCost = hotels.reduce((sum, hotel) => {
    return sum + (hotel.renovation_history?.reduce((sum, renovation) => sum + (renovation.cost || 0), 0) || 0);
  }, 0);

  // Get latest renovated hotel
  const getLatestRenovation = () => {
    let latest = { hotel: null as Hotel | null, year: 0 };

    hotels.forEach(hotel => {
      if (hotel.renovation_history && hotel.renovation_history.length > 0) {
        const maxYear = Math.max(...hotel.renovation_history.map(r => r.year));
        if (maxYear > latest.year) {
          latest = { hotel, year: maxYear };
        }
      }
    });

    return latest;
  };

  const latestRenovation = getLatestRenovation();

  // Format large numbers
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toLocaleString()}`;
  };

  if (error) {
    return (
      <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 64 }} />
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (!loading && hotels.length === 0) {
    return (
      <Box p={4} className="fade-in">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
             folio
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddHotel}
            sx={{ height: 48, borderRadius: 2 }}
          >
            Add Hotel
          </Button>
        </Box>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            py: 10,
            px: 4,
            backgroundColor: 'background.paper',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <HotelIcon sx={{ fontSize: 64, color: theme.palette.primary.main, opacity: 0.7 }} />
          <Typography variant="h5" color="text.secondary" fontWeight={500}>
            No Hotels Added Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" maxWidth={500} sx={{ mb: 2 }}>
            Start building your portfolio by adding your first hotel property. You can track performance metrics, store financial data, and get AI-powered valuation insights.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddHotel}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Add Your First Hotel
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Skeleton variant="text" width={200} height={48} />
            <Skeleton variant="text" width={150} />
          </Box>
          <Skeleton variant="rounded" width={120} height={48} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
        </Box>
        <LoadingSkeleton />
      </Box>
    );
  }

  return (
    <Box
      p={{ xs: 2, sm: 3, md: 4 }}
      sx={{
        animation: 'fadeIn 0.6s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        },
        bgcolor: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        <Typography
          variant="h4"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          <HotelIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          Hotel Portfolio
        </Typography>

        <TextField
          variant="outlined"
          placeholder="Search hotels..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          sx={{
            width: { xs: '100%', sm: '300px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.common.white, 0.9),
              transition: 'all 0.2s',
              "&:hover": {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              '&.Mui-focused': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newView) => newView && setViewMode(newView)}
          aria-label="view toggle"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableViewIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-label">Sort By</InputLabel>
            <Select
              labelId="sort-label"
              value={sortBy}
              onChange={handleSortChange}
              label="Sort By"
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="rooms">Rooms</MenuItem>
              <MenuItem value="stars">Stars</MenuItem>
              <MenuItem value="renovations">Investment</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Filter">
            <IconButton>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton onClick={() => window.location.reload()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {successMessage && (
        <Fade in={!!successMessage}>
          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.2)}`
            }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grow in={!loading} timeout={500}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                mb: 4,
                backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.secondary.dark, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    mb: 3,
                    color: theme.palette.text.primary,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} /> Portfolio Overview
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        height: '100%',
                        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.15)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        transition: 'all 0.3s',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '30%',
                          height: '100%',
                          backgroundImage: `linear-gradient(to right, transparent, ${alpha(theme.palette.primary.main, 0.08)})`,
                          zIndex: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.75rem' }}>Total Properties</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{hotels.length}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ opacity: 0.8 }}>
                            {filteredHotels.length} shown with current filters
                          </Typography>
                        </Box>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          p: 1.2,
                          borderRadius: '50%',
                          boxShadow: `0 3px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                        }}>
                          <HotelIcon color="primary" />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">Property distribution</Typography>
                          <Typography variant="caption" color="primary" fontWeight="bold">By type</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 24 }}>
                          {['Full Service', 'Limited Service', 'Extended Stay', 'Resort', 'Other'].map((_, i) => {
                            // Count how many hotels of this type
                            const count = hotels.filter(hotel =>
                              hotel.property_type?.toLowerCase() === _.toLowerCase()
                            ).length;
                            const percentage = hotels.length > 0 ? (count / hotels.length * 100) : 0;

                            return (
                              <Box
                                key={i}
                                sx={{
                                  height: `${Math.max(30, percentage)}%`,
                                  width: '18%',
                                  backgroundColor: [theme.palette.primary.main, theme.palette.secondary.main,
                                    theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main][i],
                                  mx: '1%',
                                  borderRadius: 0.5,
                                  opacity: 0.75 + (i * 0.05)
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        height: '100%',
                        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.dark, 0.15)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        transition: 'all 0.3s',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.15)}`,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '30%',
                          height: '100%',
                          backgroundImage: `linear-gradient(to right, transparent, ${alpha(theme.palette.secondary.main, 0.08)})`,
                          zIndex: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.75rem' }}>Total Rooms</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{totalRooms}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ opacity: 0.8 }}>
                            Across all properties
                          </Typography>
                        </Box>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                          p: 1.2,
                          borderRadius: '50%',
                          boxShadow: `0 3px 8px ${alpha(theme.palette.secondary.main, 0.2)}`
                        }}>
                          <MeetingRoomIcon color="secondary" />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">Average star rating</Typography>
                          <Typography variant="caption" color="secondary" fontWeight="bold">{averageStarRating.toFixed(1)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Rating value={averageStarRating} readOnly precision={0.1} size="small" />
                          <Typography variant="caption" color="textSecondary">({hotels.length} properties)</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        height: '100%',
                        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.15)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        transition: 'all 0.3s',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '30%',
                          height: '100%',
                          backgroundImage: `linear-gradient(to right, transparent, ${alpha(theme.palette.success.main, 0.08)})`,
                          zIndex: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.75rem' }}>Total Renovation Cost</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{formatNumber(totalRenovationCost)}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ opacity: 0.8 }}>
                            Across all properties
                          </Typography>
                        </Box>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.success.main, 0.15),
                          p: 1.2,
                          borderRadius: '50%',
                          boxShadow: `0 3px 8px ${alpha(theme.palette.success.main, 0.2)}`
                        }}>
                          <AttachMoneyIcon color="success" />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">Latest renovation</Typography>
                          <Typography variant="caption" color="success" fontWeight="bold">{latestRenovation.year}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">{latestRenovation.hotel?.name}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        {filteredHotels.length === 0 && !loading ? (
          <Paper
            sx={{
              p: 5,
              textAlign: 'center',
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
              border: '1px dashed',
              borderColor: alpha(theme.palette.divider, 0.3),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <HotelIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.6 }} />
            <Typography variant="h6" color="textSecondary">No hotels found</Typography>
            <Typography color="textSecondary" sx={{ mb: 1, maxWidth: '500px' }}>
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'Create your first hotel property to start tracking your portfolio'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddHotel}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Add New Hotel
              </Button>
            )}
          </Paper>
        ) : (
          <>
            {viewMode === 'table' && (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Hotel Properties ({filteredHotels.length})
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddHotel}
                      startIcon={<AddIcon />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                        boxShadow: 2
                      }}
                    >
                      Add New Hotel
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.875rem'
                  }}>
                    <thead>
                      <tr style={{
                        textAlign: 'left',
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }}>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Hotel Name</th>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Location</th>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Type</th>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Rooms</th>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Rating</th>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Renovation Cost</th>
                        <th style={{ padding: '16px 20px', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHotels.map((hotel) => {
                        const totalRenovationCost = hotel.renovation_history?.reduce((sum, renovation) => sum + renovation.cost, 0) || 0;

                        return (
                          <tr
                            key={hotel.id}
                            style={{
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.07)}`,
                              transition: 'background-color 0.2s ease',
                              cursor: 'pointer'
                            }}
                            onClick={() => navigate(`/hotels/${hotel.id}`)}
                          >
                            <td style={{ padding: '16px 20px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: getAvatarColor(hotel.name),
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {getHotelInitials(hotel.name)}
                                </Avatar>
                                <Typography fontWeight={500}>{hotel.name}</Typography>
                              </Box>
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationOnIcon fontSize="small" color="action" />
                                {hotel.city}, {hotel.state}
                              </Box>
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              {hotel.property_type && (
                                <Chip
                                  label={hotel.property_type}
                                  size="small"
                                  color={getPropertyTypeColor(hotel.property_type)}
                                  sx={{ borderRadius: 1 }}
                                />
                              )}
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MeetingRoomIcon fontSize="small" color="action" />
                                {hotel.num_rooms}
                              </Box>
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <Rating value={hotel.star_rating} readOnly precision={0.5} size="small" />
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <Typography
                                color={totalRenovationCost > 0 ? 'success.main' : 'text.secondary'}
                                fontWeight={totalRenovationCost > 0 ? 600 : 400}
                              >
                                {totalRenovationCost > 0
                                  ? `$${totalRenovationCost.toLocaleString()}`
                                  : 'None'
                                }
                              </Typography>
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/hotels/${hotel.id}`);
                                  }}
                                  sx={{
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    px: 2,
                                    py: 0.5,
                                    fontWeight: 600
                                  }}
                                >
                                  View
                                </Button>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(hotel, e);
                                  }}
                                  sx={{
                                    opacity: 0.7,
                                    '&:hover': { opacity: 1, bgcolor: 'error.light' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </Card>
            )}

            {viewMode === 'list' && (
              <Box sx={{ mt: 2 }}>
                {filteredHotels.map((hotel) => {
                  const totalRenovationCost = hotel.renovation_history?.reduce((sum, renovation) => sum + renovation.cost, 0) || 0;
                  return (
                    <Card
                      key={hotel.id}
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                        <Box
                          sx={{
                            p: 2,
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: getAvatarColor(hotel.name),
                              width: 48,
                              height: 48,
                              mr: 2,
                              boxShadow: `0 3px 5px ${alpha(theme.palette.common.black, 0.1)}`
                            }}
                          >
                            {getHotelInitials(hotel.name)}
                          </Avatar>

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {hotel.name}
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                              <Box display="flex" alignItems="center">
                                <LocationOnIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {hotel.city}, {hotel.state}
                                </Typography>
                              </Box>

                              <Box display="flex" alignItems="center">
                                <MeetingRoomIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {hotel.num_rooms} Rooms
                                </Typography>
                              </Box>

                              <Box display="flex" alignItems="center">
                                <Rating value={hotel.star_rating} readOnly precision={0.5} size="small" />
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            p: 2,
                            bgcolor: alpha(theme.palette.background.paper, 0.4),
                            borderLeft: { xs: 'none', md: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
                            borderTop: { xs: `1px solid ${alpha(theme.palette.divider, 0.1)}`, md: 'none' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, width: '100%' }}>
                            {hotel.property_type && (
                              <Chip
                                label={hotel.property_type}
                                size="small"
                                color={getPropertyTypeColor(hotel.property_type)}
                                sx={{ borderRadius: 2 }}
                              />
                            )}

                            {totalRenovationCost > 0 && (
                              <Chip
                                icon={<AttachMoneyIcon />}
                                label={totalRenovationCost.toLocaleString()}
                                size="small"
                                color="success"
                                sx={{ borderRadius: 2, fontWeight: 500 }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              mt: 'auto',
                              width: '100%',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => navigate(`/hotels/${hotel.id}`)}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                px: 3,
                                flex: 1,
                                '&:hover': {
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                }
                              }}
                            >
                              View Details
                            </Button>

                            <IconButton
                              color="error"
                              onClick={(e) => handleDeleteClick(hotel, e)}
                              sx={{
                                opacity: 0.7,
                                '&:hover': { opacity: 1, bgcolor: 'error.light' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )}

            {viewMode === 'grid' && (
              <Grid container spacing={3}>
                {filteredHotels.map((hotel: Hotel) => {
                  const hasRenovations = hotel.renovation_history && hotel.renovation_history.length > 0;
                  const avatarColor = getAvatarColor(hotel.name);

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={hotel.id}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          overflow: 'hidden',
                          transition: 'all 0.3s ease-in-out',
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }}
                      >
                        <CardHeader
                          avatar={
                            <Avatar sx={{
                              bgcolor: avatarColor,
                              boxShadow: `0 3px 5px ${alpha(theme.palette.common.black, 0.1)}`
                            }}>
                              {getHotelInitials(hotel.name)}
                            </Avatar>
                          }
                          action={
                            <Tooltip title="Delete Hotel" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleDeleteClick(hotel, e)}
                                sx={{
                                  opacity: 0.7,
                                  '&:hover': { opacity: 1, bgcolor: 'error.light' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                          title={
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.2 }}>
                              {hotel.name}
                            </Typography>
                          }
                          subheader={
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                              <LocationOnIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {hotel.city}, {hotel.state}
                              </Typography>
                            </Box>
                          }
                          sx={{ pb: 1 }}
                        />

                        <Divider />

                        <CardContent sx={{ pt: 2, pb: 1, flexGrow: 1 }}>
                          <Stack direction="row" spacing={1} mb={2}>
                            <Chip
                              icon={<MeetingRoomIcon />}
                              label={`${hotel.num_rooms} Rooms`}
                              size="small"
                              color="primary"
                              sx={{ borderRadius: 2 }}
                            />

                            <Box sx={{ ml: 'auto' }}>
                              <Rating value={hotel.star_rating} readOnly precision={0.5} size="small" />
                            </Box>
                          </Stack>

                          {hotel.property_type && (
                            <Chip
                              label={hotel.property_type}
                              size="small"
                              color={getPropertyTypeColor(hotel.property_type)}
                              sx={{ borderRadius: 2, mb: 2 }}
                            />
                          )}

                          <Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <HistoryIcon fontSize="small" />
                                Renovation History
                              </Typography>

                              {hasRenovations && (
                                <Badge
                                  badgeContent={hotel.renovation_history?.length}
                                  color="primary"
                                  overlap="circular"
                                >
                                  <Tooltip title="View Renovation History">
                                    <IconButton
                                      onClick={() => hotel.id && handleExpandClick(hotel.id)}
                                      size="small"
                                      sx={{
                                        transform: hotel.id && typeof hotel.id === 'string' && expanded[hotel.id] ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 0.2s',
                                        bgcolor: hotel.id && typeof hotel.id === 'string' && expanded[hotel.id] ? 'action.selected' : 'transparent'
                                      }}
                                    >
                                      <ExpandMoreIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Badge>
                              )}
                            </Box>

                            {hasRenovations && hotel.id && typeof hotel.id === 'string' && !expanded[hotel.id] && (
                              <Box sx={{ mt: 1 }}>
                                <Tooltip title="Total renovation investment">
                                  <Chip
                                    icon={<AttachMoneyIcon />}
                                    label={`${hotel.renovation_history?.reduce((sum, renovation) => sum + renovation.cost, 0).toLocaleString()}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                  />
                                </Tooltip>
                              </Box>
                            )}

                            <Collapse in={hotel.id && typeof hotel.id === 'string' ? expanded[hotel.id] : false} timeout="auto" unmountOnExit>
                              <Paper variant="outlined" sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                                {hasRenovations && hotel.renovation_history ? (
                                  <>
                                    {hotel.renovation_history.map((renovation, index) => (
                                      <Box key={index} sx={{ mb: index < hotel.renovation_history!.length - 1 ? 1.5 : 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {renovation.year}: {renovation.description}
                                        </Typography>
                                        <Typography variant="body2" color="success.main" fontWeight={500}>
                                          ${renovation.cost.toLocaleString()}
                                        </Typography>
                                        {index < hotel.renovation_history!.length - 1 && (
                                          <Divider sx={{ my: 1.5, opacity: 0.5 }} />
                                        )}
                                      </Box>
                                    ))}
                                    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AttachMoneyIcon fontSize="small" />
                                        Total: ${hotel.renovation_history.reduce((sum, renovation) => sum + renovation.cost, 0).toLocaleString()}
                                      </Typography>
                                    </Box>
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" align="center">
                                    No renovations recorded
                                  </Typography>
                                )}
                              </Paper>
                            </Collapse>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ p: 0, mt: 'auto' }}>
                          <Button
                            fullWidth
                            onClick={() => navigate(`/hotels/${hotel.id}`)}
                            sx={{
                              p: 1.5,
                              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.9),
                                color: 'primary.contrastText',
                              },
                            }}
                          >
                            View Details
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            maxWidth: 'sm',
            width: '100%'
          }
        }}
        TransitionProps={{
          enter: true,
          exit: true
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          pt: 3,
          px: 3,
          fontWeight: 600,
          fontSize: '1.5rem',
          color: 'error.main'
        }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Avatar
              sx={{
                bgcolor: 'error.light',
                width: 64,
                height: 64,
                mb: 2
              }}
            >
              <DeleteIcon fontSize="large" sx={{ color: 'error.main' }} />
            </Avatar>
          </Box>
          <DialogContentText sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete <strong>"{deleteDialog.hotelName}"</strong>?
          </DialogContentText>
          <DialogContentText sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'text.secondary',
            bgcolor: 'action.hover',
            borderRadius: 2,
            p: 2
          }}>
            This action cannot be undone. All associated data including renovation history,
            financial records, and reports will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={closeDeleteDialog}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'background.paper',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: theme.shadows[3],
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: theme.shadows[5],
                transform: 'translateY(-2px)'
              }
            }}
          >
            Delete Hotel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelList;
